import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './BatchProcessDetails.module.css';

// Helper function to format dates nicely
const formatDate = (dateString: string | null | undefined, includeTime: boolean = false): string => {
  if (!dateString || dateString.startsWith('0001-01-01')) return '-';
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  };
  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    // to match previous behavior (24h without suffix)
    // locale option hour12 is respected by some environments for toLocaleString
    (options as any).hour12 = false;
  }
  try {
    return new Date(dateString).toLocaleDateString('en-GB', options).replace(/,/g, '');
  } catch (error) {
    return dateString as string;
  }
};

const BatchProcessDetails: React.FC = () => {
  const navigate = useNavigate();
  const { processType } = useParams<{ processType?: string }>(); // e.g., "Dumping" from the URL
  const location = useLocation();
  const state: any = (location as any).state;
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    // The 'data' object is now the 'state' from the location
    const incoming = state as any;
    // eslint-disable-next-line no-console
    console.log('Batch process details:', incoming);
    setData(incoming);
  }, [state]);

  // Handle case where the user navigates directly to this URL without state
  if (!data) {
    return (
      <div className="pageWrapper">
        <h2>No batch data found. Please return to the batches list.</h2>
      </div>
    );
  }
  
  // Find the specific process details within the first product
  const product = data.batchProducts?.[0];
  const processDetails = product?.productProcesses?.find((p: any) => p.processType === processType);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="pageWrapper">
      <div className="contentWrapper">
        <header className="header">
          <h1 className="title">Batch-{data.batchkey}</h1>
          <p className="breadcrumbs">
            Processing Batches &gt; <span className='removed-slash'>Batch Details</span> &gt; {processType || 'Details'}
          </p>
        </header>

        <main>
          <section className="card">
            <h2 className="sectionTitle">Batch Details</h2>
            <div className="detailsGrid">
              <div className="detailItem">
                <label>Batch Name</label>
                <span>{data.batchName || '-'}</span>
              </div>
              <div className="detailItem">
                <label>Assigned Manager ID</label>
                <span>{data.assignedManagerId?.username || '-'}</span>
              </div>
              <div className="detailItem">
                <label>Plant</label>
                <span>{data.plantId || '-'}</span>
              </div>
              <div className="detailItem">
                <label>Created By</label>
                <span>{data.createdBy?.username || '-'}</span>
              </div>
              <div className="detailItem">
                <label>Status</label>
                <span>{data.status || '-'}</span>
              </div>
              <div className="detailItem">
                <label>Created Date</label>
                <span>{formatDate(processDetails?.createdDate, true) || '-'}</span>
              </div>
              <div className="detailItem">
                <label>Total LOTS</label>
                <span>{data.batchLots?.length ?? 0}</span>
              </div>
              <div className="detailItem">
                <label>Total Products</label>
                <span>{data.batchProducts?.length ?? 0}</span>
              </div>
            </div>
          </section>

          {product && (
            <section className="card">
              <h2 className="sectionTitle">Product Details</h2>
              <div className="detailsGrid">
                <div className="detailItem">
                  <label>Task Type</label>
                  <span>{data.taskType || '-'}</span>
                </div>
                <div className="detailItem">
                  <label>Product Name</label>
                  <span>{product.productName || '-'}</span>
                </div>
                <div className="detailItem">
                  <label>Product Lot Number</label>
                  <span>{product.finl_lot_id || '-'}</span>
                </div>
                <div className="detailItem">
                  <label>Expected Output Weight</label>
                  <span>{product.expectedOutputWeight ? `${product.expectedOutputWeight}kg` : '-'}</span>
                </div>
                <div className="detailItem">
                  <label>Task Assigned Date</label>
                  <span>{formatDate(processDetails?.tasks?.[0]?.startDate) || '-'}</span>
                </div>
              </div>
            </section>
          )}

          <section className="card">
            <h2 className="sectionTitle">{data.taskType} Logs</h2>
            <div className="tableSection">
              <div className="tableHeader">
                <span>S.No</span>
                <span>Processing date</span>
                <span>Bag(s) Input</span>
                <span>Note</span>
                <span>Employee Name</span>
              </div>
              <div className="tableBody">
                {data.allLogs.length > 0 ? (
                  data.allLogs.map((log: any, index: number) => (
                    <div key={log.logkey} className="tableRow">
                      <span>{index + 1}</span>
                      <span>{formatDate(log.logDate, true)}</span>
                      <span>{log.cleanedBaggagesCount ?? '-'}</span>
                      <span>{log.notes || '-'}</span>
                      <span>{log.createdBy?.username || '-'}</span>
                    </div>
                  ))
                ) : (
                  <div className="noDataRow">No logs available for this process.</div>
                )}
              </div>
            </div>
          </section>
        </main>
        
        <footer className="footer">
          <button onClick={handleBack} className="backButton">Back</button>
        </footer>
      </div>
    </div>
  );
};

export default BatchProcessDetails;

